import { CustomizeCheckoutData } from '@/@types/Checkouts/edit-checkout'
import { toast } from '@/hooks/use-toast'
import { isCheckoutTemplate } from '@/utils/checkoutTemplates'
import { TFunction } from 'i18next'
import React from 'react'
import { UseFormSetValue } from 'react-hook-form'

function hasWhatsAppSupportNumber(
  whatsappSupport: string | null | undefined,
): boolean {
  return whatsappSupport != null && String(whatsappSupport).trim() !== ''
}

export function resolveShowWhatsAppSupport(checkout: {
  show_whatsapp_support?: boolean | null
  showw_whatsapp_suport?: boolean | null
  show_whatsapp_suport?: boolean | null
  whatsapp_support?: string | null
} | null | undefined): boolean {
  if (!hasWhatsAppSupportNumber(checkout?.whatsapp_support)) {
    return false
  }

  const explicitFlag =
    checkout?.show_whatsapp_support ??
    checkout?.showw_whatsapp_suport ??
    checkout?.show_whatsapp_suport

  if (explicitFlag !== undefined && explicitFlag !== null) {
    return Boolean(explicitFlag)
  }

  return true
}

const BANNER_IMAGE_FIELDS = [
  'banner_mobile_top_image',
  'banner_mobile_bottom_image',
  'banner_desktop_top_image',
  'banner_desktop_bottom_image',
  'banner_desktop_side_image',
] as const

const BANNER_FIELD_PAIRS = [
  ['banner_desktop_top_is_visible', 'banner_desktop_top_image'],
  ['banner_desktop_bottom_is_visible', 'banner_desktop_bottom_image'],
  ['banner_desktop_side_is_visible', 'banner_desktop_side_image'],
  ['banner_mobile_top_is_visible', 'banner_mobile_top_image'],
  ['banner_mobile_bottom_is_visible', 'banner_mobile_bottom_image'],
] as const

const BANNER_IMAGE_REQUIREMENTS = [
  {
    visible: 'banner_desktop_top_is_visible',
    image: 'banner_desktop_top_image',
    tab: 'banners',
  },
  {
    visible: 'banner_desktop_bottom_is_visible',
    image: 'banner_desktop_bottom_image',
    tab: 'banners',
  },
  {
    visible: 'banner_desktop_side_is_visible',
    image: 'banner_desktop_side_image',
    tab: 'banners',
  },
  {
    visible: 'banner_mobile_top_is_visible',
    image: 'banner_mobile_top_image',
    tab: 'banners',
  },
  {
    visible: 'banner_mobile_bottom_is_visible',
    image: 'banner_mobile_bottom_image',
    tab: 'banners',
  },
] as const

export type BannerImageField =
  (typeof BANNER_IMAGE_REQUIREMENTS)[number]['image']

export function hasBannerImage(value: unknown): boolean {
  if (value instanceof File) return true
  return typeof value === 'string' && value.trim().length > 0
}

export function findFirstMissingBannerImage(data: CustomizeCheckoutData) {
  for (const rule of BANNER_IMAGE_REQUIREMENTS) {
    if (data[rule.visible] === '1' && !hasBannerImage(data[rule.image])) {
      return rule
    }
  }

  return null
}

export function prepareCustomizeCheckoutPayload(
  data: CustomizeCheckoutData,
): CustomizeCheckoutData {
  const payload = { ...data }

  if (!payload.show_urgency) {
    delete payload.timer_in_minutes
  }

  if (!isCheckoutTemplate(payload.template)) {
    delete payload.template
  }

  for (const [visibleKey, imageKey] of BANNER_FIELD_PAIRS) {
    if (payload[visibleKey] === '0' || !(payload[imageKey] instanceof File)) {
      delete payload[imageKey]
    }
  }

  return payload
}

function isFileSizeError(message: string | undefined): boolean {
  const text = (message ?? '').toLowerCase()
  return (
    text.includes('4096') ||
    text.includes('kilobyte') ||
    text.includes('kb') ||
    text.includes('maior') ||
    text.includes('large')
  )
}

export function getCustomizeCheckoutFieldErrorMessage(
  field: string,
  fallback: string | undefined,
  t: TFunction,
): string {
  if (field === 'timer_in_minutes') {
    return t('checkouts:timer_min_one_minute')
  }

  if (field === 'urgency_description') {
    return t('common:validation.maxLength', { max: 255 })
  }

  if (
    field === 'product_photo' ||
    BANNER_IMAGE_FIELDS.includes(field as (typeof BANNER_IMAGE_FIELDS)[number])
  ) {
    if (isFileSizeError(fallback)) {
      return t('common:file_too_large', { size: 4 })
    }

    return t('common:invalid_file_type')
  }

  return fallback || t('common:data_submission_error')
}

export function getCustomizeCheckoutErrorMessage(
  error: {
    response?: {
      status?: number
      data?: { message?: string; errors?: Record<string, string[]> }
    }
  },
  t: TFunction,
): string {
  if (error.response?.status === 413) {
    return t('common:file_too_large', { size: 4 })
  }

  const fieldErrors = error.response?.data?.errors
  const firstField = fieldErrors ? Object.keys(fieldErrors)[0] : undefined

  if (firstField) {
    return getCustomizeCheckoutFieldErrorMessage(
      firstField,
      fieldErrors?.[firstField]?.[0],
      t,
    )
  }

  return error.response?.data?.message || t('common:data_submission_error')
}

const CHECKOUT_IMAGE_MAX_BYTES = 4 * 1024 * 1024
const PRODUCT_PHOTO_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/webp',
]

function resetFileInput(event: React.ChangeEvent<HTMLInputElement>) {
  event.target.value = ''
}

export function handleProductPhotoFileChange(
  event: React.ChangeEvent<HTMLInputElement>,
  setValue: UseFormSetValue<CustomizeCheckoutData>,
  t: TFunction,
) {
  const file = event.target.files?.[0]
  if (!file) {
    return
  }

  if (!PRODUCT_PHOTO_TYPES.includes(file.type)) {
    toast({
      variant: 'error',
      description: t('checkouts:product_photo_invalid_type'),
    })
    resetFileInput(event)
    return
  }

  if (file.size > CHECKOUT_IMAGE_MAX_BYTES) {
    toast({
      variant: 'error',
      description: t('common:file_too_large', { size: 4 }),
    })
    resetFileInput(event)
    return
  }

  setValue('product_photo', file)
  setValue('remove_product_photo', false)
}

const BANNER_PHOTO_TYPES = ['image/jpeg', 'image/jpg', 'image/png']

export function handleBannerFileChange(
  event: React.ChangeEvent<HTMLInputElement>,
  fieldName: keyof CustomizeCheckoutData,
  setValue: UseFormSetValue<CustomizeCheckoutData>,
  t: TFunction,
  clearErrors?: (name?: BannerImageField) => void,
) {
  const file = event.target.files?.[0]
  if (!file) {
    return
  }

  if (!BANNER_PHOTO_TYPES.includes(file.type)) {
    toast({
      variant: 'error',
      description: t('common:invalid_file_type'),
    })
    resetFileInput(event)
    return
  }

  if (file.size > CHECKOUT_IMAGE_MAX_BYTES) {
    toast({
      variant: 'error',
      description: t('common:file_too_large', { size: 4 }),
    })
    resetFileInput(event)
    return
  }

  setValue(fieldName, file as never)
  clearErrors?.(fieldName as BannerImageField)
}
